/**
 * @file CanCheckPanel.jsx
 * @description The installation's CHECKING step: the machine model's CAN
 *              frames (machine_can_ids, seeded from the OEM protocol sheets)
 *              against what the TCU is receiving RIGHT NOW — green tick per
 *              frame arriving, red cross per frame missing, so the installer
 *              can see exactly which part of the bus to debug. Polls every 5s
 *              while wiring. Frames arriving that the table doesn't list are
 *              shown too (wrong bus / missing model row — both look like that).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Box, Typography, Stack, Chip, Skeleton, Alert, Tooltip,
} from '@mui/material';
import {
    CheckCircle as OkIcon, Cancel as MissIcon, Memory as CanIcon,
} from '@mui/icons-material';
import hardwareLifecycleService from '../../../services/hardwareLifecycleService';
import { formatToIST } from '../../../utils/dataUtils';

const CanCheckPanel = ({ deviceId, equipmentId }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    const poll = useCallback(async () => {
        try {
            const r = await hardwareLifecycleService.canCheck(deviceId, equipmentId);
            setData(r);
            setError('');
        } catch (e) {
            setError(e.message || 'CAN check failed');
        }
    }, [deviceId, equipmentId]);

    useEffect(() => {
        if (!deviceId || !equipmentId) return;
        poll();
        const t = setInterval(poll, 5000);
        return () => clearInterval(t);
    }, [poll, deviceId, equipmentId]);

    if (!deviceId || !equipmentId) return null;

    const checks = data?.checks || [];
    const got = checks.filter(c => c.received).length;

    return (
        <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5, mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                <CanIcon color="primary" sx={{ fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={800}>
                    CAN check{data?.model ? ` — ${data.model}` : ''}
                </Typography>
                {checks.length > 0 && (
                    <Chip size="small" label={`${got}/${checks.length} arriving`}
                        color={got === checks.length ? 'success' : got > 0 ? 'warning' : 'error'}
                        sx={{ fontWeight: 800 }} />
                )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {data?.families?.length
                    ? `${data.families.join(' · ')} protocol — every frame below must arrive (window: last ${data.window_min} min).`
                    : 'Frames the machine must transmit once the CAN tap is right.'}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

            {data === null ? (
                <Stack spacing={1}>{[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={40} />)}</Stack>
            ) : !data.applicable ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                    No CAN ID table for {data.model ? <strong>{data.model}</strong> : 'this model'} yet —
                    the machine_can_ids table carries Dingli K120 RT scissors, AC scissors, BA16/18NE and
                    the D-series boom protocols. Frames actually arriving are listed below.
                </Alert>
            ) : (
                <Stack spacing={0.75}>
                    {checks.map((c) => (
                        <Box key={`${c.family}-${c.can_id}`}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1.25,
                                px: 1.25, py: 0.75, borderRadius: '10px',
                                bgcolor: c.received ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.05)',
                                border: '1px solid',
                                borderColor: c.received ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.3)',
                            }}>
                            {c.received
                                ? <OkIcon sx={{ color: '#059669', fontSize: 20 }} />
                                : <MissIcon sx={{ color: '#dc2626', fontSize: 20 }} />}
                            <Chip size="small" label={c.can_id}
                                sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 11, height: 22 }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" fontWeight={700} noWrap>{c.message_name}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}
                                    title={c.signals || ''}>
                                    {c.signals || c.family_label}
                                </Typography>
                            </Box>
                            <Typography variant="caption" color={c.received ? 'text.secondary' : 'error.main'}
                                sx={{ fontWeight: 700, flexShrink: 0 }}>
                                {c.received
                                    ? (c.last_seen ? formatToIST(c.last_seen, 'HH:mm:ss') : 'received')
                                    : 'not received'}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            )}

            {data?.unexpected_keys?.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" fontWeight={800} color="text.secondary">
                        Also receiving (not in the model's table):
                    </Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {data.unexpected_keys.map(k => (
                            <Tooltip key={k} title="Frame arriving that the model's CAN table doesn't list — wrong bus tap, or a frame worth adding to the table">
                                <Chip size="small" variant="outlined" label={k}
                                    sx={{ fontFamily: 'monospace', fontSize: 10.5, height: 20 }} />
                            </Tooltip>
                        ))}
                    </Stack>
                </Box>
            )}
        </Card>
    );
};

export default CanCheckPanel;
